import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { HavePaginationEntity } from 'src/entities/HavePagination.entity';
import { ProductEntity as Entity } from 'src/entities/ProductEntity';
import { fetchAllPages, PagedResult } from 'src/utils/fetchAllPages';

const requestPathBase = '/api/v4/master/products';
export const queryKey = 'useProductsRequest';

const getResources = async (page: number, sortCondition: string, searchCondition: string, per?: number) => {
  const requestParams = [
    `page=${page + 1}`,
    sortCondition ? `sort_condition=${sortCondition}` : '',
    searchCondition ? `${searchCondition}` : '',
    per ? `per=${per}` : '',
  ].filter((maybe) => maybe).join('&');

  const requestPath = [
    requestPathBase,
    requestParams,
  ].join('?');

  const response: AxiosResponse<HavePaginationEntity<Entity>> = await axios.get<HavePaginationEntity<Entity>>(requestPath);

  return response.data;
};

export const useProductsRequest = (
  page: number,
  sortCondition: string,
  searchCondition: string,
  per?: number,
  options?: Omit<UseQueryOptions<HavePaginationEntity<Entity>>, 'queryKey' | 'queryFn'>
) => useQuery({
  queryKey: [queryKey, { page, sortCondition, searchCondition, per }],
  queryFn: () => getResources(page, sortCondition, searchCondition, per),
  ...options
});

// getResources は 0 origin（内部で page + 1 して API に投げる）だが fetchAllPages は
// 1 origin なので、ここで変換する。総ページ数のフィールド名も totalPageCount で
// v2/v5 API の total_pages_count とは異なる。
const fetchProductsPage = (sortCondition: string, searchCondition: string, per?: number) =>
  async (page: number): Promise<PagedResult<Entity>> => {
    const response = await getResources(page - 1, sortCondition, searchCondition, per);

    return { items: response.data, totalPagesCount: response.totalPageCount };
  };

export const useAllProductsRequest = (sortCondition: string, searchCondition: string, enabled: boolean, per?: number) => useQuery({
  queryKey: [queryKey, { sortCondition, searchCondition, per }],
  queryFn: () => fetchAllPages(fetchProductsPage(sortCondition, searchCondition, per)),
  staleTime: Infinity,
  enabled,
  // 再試行は fetchAllPages がページ単位で行う。ここで retry すると
  // 成功済みのページまで取り直してしまうため無効化する。
  retry: false,
});
